<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

interface Props {
  options: Array<{ value: string; label: string; disabled?: boolean; checked?: boolean; isGenerated?: boolean; recommended?: boolean }>
  modelValue?: string | string[]
  placeholder?: string
  disabled?: boolean
  multiple?: boolean
  maxSelections?: number
  totalUsedTones?: number // Total tones used (selected + generated)
  showGenerateButton?: boolean // Show "Generate tones with AI" button
  showSingleAsPill?: boolean // Show single selection as pill (for AI models)
  // New categorized tones
  yourTones?: Array<{ value: string; label: string; disabled?: boolean; checked?: boolean }>
  aiGeneratedTones?: Array<{ value: string; label: string; disabled?: boolean; checked?: boolean }>
  existingTones?: Array<{ value: string; label: string; disabled?: boolean; checked?: boolean }>
  isGeneratingTones?: boolean // Loading state for AI tone generation
  isGenerateButtonDisabled?: boolean // Disable the generate button (e.g., after successful generation)
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  modelValue: '',
  placeholder: 'Select an option',
  disabled: false,
  multiple: false,
  maxSelections: 5,
  totalUsedTones: 0,
  showGenerateButton: false,
  showSingleAsPill: false,
  yourTones: () => [],
  aiGeneratedTones: () => [],
  existingTones: () => [],
  isGeneratingTones: false,
  isGenerateButtonDisabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: string | string[]]
  'generate-tones': []
}>()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement>()

// Computed properties for multiple selection
const selectedValues = computed(() => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) ? props.modelValue : []
  }
  return []
})

const displayText = computed(() => {
  if (props.multiple) {
    const selected = selectedValues.value
    if (selected.length === 0) return props.placeholder
    if (selected.length === 1) {
      const option = props.options.find(opt => opt.value === selected[0])
      return option?.label || selected[0]
    }
    return `${selected.length} selected`
  }
  return props.modelValue || props.placeholder
})

// Get selected options for display
const selectedOptions = computed(() => {
  if (!props.multiple) return []

  // Combine all possible option sources
  const allOptions = [
    ...props.options,
    ...(props.yourTones || []),
    ...(props.aiGeneratedTones || []),
    ...(props.existingTones || [])
  ]

  return selectedValues.value.map(value => {
    // Try to find the option in all available sources
    return allOptions.find(opt => opt.value === value)
  }).filter(Boolean)
})

// Get selected single option for pill display
const selectedSingleOption = computed(() => {
  if (props.multiple || !props.modelValue) return null
  return props.options.find(opt => opt.value === props.modelValue)
})

const isSelected = (value: string) => {
  if (props.multiple) {
    return selectedValues.value.includes(value)
  }
  return props.modelValue === value
}

const isMaxReached = computed(() => {
  if (!props.multiple) return false
  // Use totalUsedTones if provided, otherwise fall back to selectedValues length
  const totalUsed = props.totalUsedTones > 0 ? props.totalUsedTones : selectedValues.value.length
  return totalUsed >= props.maxSelections
})

// Separate generated and existing tones
const generatedTones = computed(() => {
  return props.options.filter(option => option.isGenerated)
})

const existingTones = computed(() => {
  return props.options.filter(option => !option.isGenerated)
})

const hasGeneratedTones = computed(() => {
  return generatedTones.value.length > 0
})

const hasYourTones = computed(() => {
  return props.yourTones && props.yourTones.length > 0
})

const hasAIGeneratedTones = computed(() => {
  return props.aiGeneratedTones && props.aiGeneratedTones.length > 0
})

const hasExistingTones = computed(() => {
  console.log('🔍 hasExistingTones computed:', {
    existingTones: props.existingTones,
    length: props.existingTones?.length,
    hasData: props.existingTones && props.existingTones.length > 0
  })
  return props.existingTones && props.existingTones.length > 0
})

const isOptionDisabled = (value: string) => {
  // Check if option has disabled property in any of the option sources
  const allOptions = [
    ...props.options,
    ...(props.yourTones || []),
    ...(props.aiGeneratedTones || []),
    ...(props.existingTones || [])
  ]

  const option = allOptions.find(opt => opt.value === value)
  // If option is explicitly disabled (e.g., content already generated), don't allow any interaction
  if (option?.disabled) return true

  if (!props.multiple) return false
  // If already selected, it's not disabled (can be deselected) - unless explicitly disabled above
  if (isSelected(value)) return false
  // If max reached and not selected, it's disabled
  return isMaxReached.value
}

const handleSelect = (value: string) => {
  // Don't allow selection of disabled options
  if (isOptionDisabled(value)) {
    return
  }

  if (props.multiple) {

    const currentValues = [...selectedValues.value]
    const index = currentValues.indexOf(value)

    if (index > -1) {
      // Remove if already selected
      currentValues.splice(index, 1)
    } else {
      // Add if not selected
      currentValues.push(value)
    }

    emit('update:modelValue', currentValues)
  } else {
    emit('update:modelValue', value)
    isOpen.value = false
  }
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

// Close dropdown when pressing Escape key
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isOpen.value = false
  }
}

// Add event listeners when dropdown opens
const handleToggle = () => {
  if (!isOpen.value) {
    // Close all other dropdowns first, then open this one
    document.dispatchEvent(new CustomEvent('closeAllDropdowns'))

    // Use nextTick to ensure other dropdowns are closed before opening this one
    setTimeout(() => {
      isOpen.value = true
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('keydown', handleKeydown)
    }, 10)
  } else {
    isOpen.value = false
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
  }
}

// Listen for the custom event to close this dropdown
const handleCloseAllDropdowns = () => {
  if (isOpen.value) {
    isOpen.value = false
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
  }
}

onMounted(() => {
  document.addEventListener('closeAllDropdowns', handleCloseAllDropdowns)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('closeAllDropdowns', handleCloseAllDropdowns)
})
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      type="button"
      class="w-full px-4 py-2 text-left border rounded-lg focus:outline-none shadow-sm min-h-[42px] flex items-center"
      :class="[
        disabled ? 'border-gray-300' : isOpen ? 'border-[#8E4EC6]' : 'border-[#EAE7EC]',
        isOpen ? 'border-[#8E4EC6]' : 'border-[#EAE7EC]'
      ]"
      :disabled="disabled"
      @click="handleToggle"
    >
      <!-- Multiple selection pills -->
      <div v-if="multiple && selectedOptions.length > 0" class="flex flex-wrap gap-2 flex-1">
        <span
          v-for="option in selectedOptions"
          :key="option?.value"
          class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#8E4EC6] text-white"
        >
          {{ option?.label }}
          <!-- Only show cross button if option is not disabled (content not generated) -->
          <button
            v-if="!option?.disabled"
            @click.stop="handleSelect(option?.value || '')"
            class="ml-2 text-white hover:text-gray-200 focus:outline-none"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </span>
      </div>
      <!-- Fallback: Show pills even when exact matching fails -->
      <div v-else-if="multiple && selectedValues.length > 0" class="flex flex-wrap gap-2 flex-1">
        <span
          v-for="value in selectedValues"
          :key="value"
          class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#8E4EC6] text-white"
        >
          {{ value }}
          <!-- Only show cross button if value is not disabled (content not generated) -->
          <button
            v-if="!isOptionDisabled(value)"
            @click.stop="handleSelect(value)"
            class="ml-2 text-white hover:text-gray-200 focus:outline-none"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </span>
      </div>
      <!-- Single selection as pill -->
      <div v-else-if="!multiple && showSingleAsPill && selectedSingleOption" class="flex items-center flex-1">
        <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#8E4EC6] text-white">
          {{ selectedSingleOption.label }}
        </span>
      </div>
      <!-- Regular text display -->
      <span v-else :class="modelValue ? 'text-gray-900' : 'text-[#00051D] opacity-[0.4549]'">{{ displayText }}</span>
    </button>

    <div
      v-if="isOpen"
      class="absolute z-10 w-full mt-1 bg-white border border-[#8E4EC6] rounded-lg shadow-lg max-h-60 overflow-y-auto"
    >
      <!-- Generate tones with AI button at top -->
      <div
        v-if="showGenerateButton"
        class="px-4 py-3 border-b border-gray-200"
      >
        <button
          @click="$emit('generate-tones')"
          :disabled="isGeneratingTones || isGenerateButtonDisabled"
          class="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-[#8E4EC6] bg-white border border-[#8E4EC6] rounded-lg hover:bg-[#F3E8FF] hover:border-[#A855F7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            v-if="isGeneratingTones"
            class="animate-spin w-4 h-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <svg
            v-else
            class="w-4 h-4 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          {{ isGeneratingTones ? 'Generating tones...' : isGenerateButtonDisabled ? 'Tones Generated' : 'Generate tones with AI' }}
        </button>
      </div>

      <!-- Max selections message at top -->
      <div v-if="isMaxReached" class="px-4 py-2 text-sm text-red-600 bg-red-50 border-b border-red-200">
        Maximum {{ maxSelections }} selection allowed
      </div>

      <!-- YOUR TONES Section -->
      <template v-if="hasYourTones">
        <div class="px-4 py-2 text-sm font-semibold text-gray-700 uppercase tracking-wide bg-gray-50">
          YOUR TONES
        </div>

        <div
          v-for="option in yourTones"
          :key="option.value"
          class="px-4 py-2 flex items-center space-x-3"
          :class="[
            isOptionDisabled(option.value)
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-100 cursor-pointer'
          ]"
          @click="handleSelect(option.value)"
        >
          <div
            v-if="multiple"
            class="flex items-center justify-center w-4 h-4 border-2 rounded"
            :class="[
              isSelected(option.value) || option.checked
                ? 'bg-[#8E4EC6] border-[#8E4EC6]'
                : isOptionDisabled(option.value)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-300'
            ]"
          >
            <svg
              v-if="isSelected(option.value) || option.checked"
              class="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <div class="flex-1 flex items-center">
            <span>{{ option.label }}</span>
          </div>
        </div>
      </template>

      <!-- AI GENERATED TONES Section -->
      <template v-if="hasAIGeneratedTones">
        <div class="px-4 py-2 text-sm font-semibold text-gray-700 uppercase tracking-wide bg-gray-50">
          AI GENERATED TONES
        </div>

        <div
          v-for="option in aiGeneratedTones"
          :key="option.value"
          class="px-4 py-2 flex items-center space-x-3"
          :class="[
            isOptionDisabled(option.value)
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-100 cursor-pointer'
          ]"
          @click="handleSelect(option.value)"
        >
          <div
            v-if="multiple"
            class="flex items-center justify-center w-4 h-4 border-2 rounded"
            :class="[
              isSelected(option.value) || option.checked
                ? 'bg-[#8E4EC6] border-[#8E4EC6]'
                : isOptionDisabled(option.value)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-300'
            ]"
          >
            <svg
              v-if="isSelected(option.value) || option.checked"
              class="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <div class="flex-1 flex items-center">
            <span>{{ option.label }}</span>
          </div>
        </div>
      </template>

      <!-- EXISTING TONES Section -->
      <template v-if="hasExistingTones">
        <div class="px-4 py-2 text-sm font-semibold text-gray-700 uppercase tracking-wide bg-gray-50">
          EXISTING TONES
        </div>

        <div
          v-for="option in props.existingTones"
          :key="option.value"
          class="px-4 py-2 flex items-center space-x-3"
          :class="[
            isOptionDisabled(option.value)
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-100 cursor-pointer'
          ]"
          @click="handleSelect(option.value)"
        >
          <div
            v-if="multiple"
            class="flex items-center justify-center w-4 h-4 border-2 rounded"
            :class="[
              isSelected(option.value) || option.checked
                ? 'bg-[#8E4EC6] border-[#8E4EC6]'
                : isOptionDisabled(option.value)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-300'
            ]"
          >
            <svg
              v-if="isSelected(option.value) || option.checked"
              class="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <div class="flex-1 flex items-center">
            <span>{{ option.label }}</span>
          </div>
        </div>
      </template>

      <!-- Generated Tones Section (for categorized tones with isGenerated flag) -->
      <template v-if="hasGeneratedTones">
        <!-- Generated Tones Options -->
        <div
          v-for="option in generatedTones"
          :key="option.value"
          class="px-4 py-2 flex items-center space-x-3"
          :class="[
            isOptionDisabled(option.value)
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-100 cursor-pointer'
          ]"
          @click="handleSelect(option.value)"
        >
          <!-- Checkbox for multiple selection -->
          <div
            v-if="multiple"
            class="flex items-center justify-center w-4 h-4 border-2 rounded"
            :class="[
              isSelected(option.value) || option.checked
                ? 'bg-[#8E4EC6] border-[#8E4EC6]'
                : isOptionDisabled(option.value)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-300'
            ]"
          >
            <svg
              v-if="isSelected(option.value) || option.checked"
              class="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <!-- Option label with recommended tag -->
          <div class="flex-1 flex items-center">
            <span>{{ option.label }}</span>
            <span
              v-if="option.recommended"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-2"
            >
              Recommended
            </span>
          </div>
        </div>
      </template>

      <!-- Regular Options Section (for AI models and other non-categorized options) -->
      <template v-if="!hasYourTones && !hasAIGeneratedTones && !hasExistingTones && !hasGeneratedTones && options.length > 0">
        <div
          v-for="option in options"
          :key="option.value"
          class="px-4 py-2 flex items-center space-x-3"
          :class="[
            isOptionDisabled(option.value)
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-100 cursor-pointer'
          ]"
          @click="handleSelect(option.value)"
        >
          <!-- Checkbox for multiple selection -->
          <div
            v-if="multiple"
            class="flex items-center justify-center w-4 h-4 border-2 rounded"
            :class="[
              isSelected(option.value) || option.checked
                ? 'bg-[#8E4EC6] border-[#8E4EC6]'
                : isOptionDisabled(option.value)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-300'
            ]"
          >
            <svg
              v-if="isSelected(option.value) || option.checked"
              class="w-3 h-3 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <!-- Option label with recommended tag -->
          <div class="flex-1 flex items-center justify-between">
            <span>{{ option.label }}</span>
            <span
              v-if="option.recommended"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 ml-2"
            >
              Recommended
            </span>
          </div>
        </div>
      </template>


    </div>
  </div>
</template>
